import { useContext, useCallback } from 'react';
import { useDisclosure } from '@ifixit/ui/hooks/use-disclosure';
import { connect } from 'react-redux';
import styled, { css } from 'styled-components';
import { space, shadow } from '@ifixit/primitives';
import { withBreakpoints, BreakpointsContext } from 'Shared/utils/breakpoints';
import FocusLock from 'react-focus-lock';
import { Logo, ProLogo, MenuIcon, Headings, MobileMenu, Dropdowns } from './components';
import InstantSearchBar from './components/instant_search_bar';
import InstantMobileSearch from './components/mobile_search/instant-search';
import * as HeaderActions from './actions';
import { useModalIsOpen } from './shared-state';
// Mirror header style changes in `ifixit_minimal_header_styles.css` to avoid CLS

const HeaderWrapper = styled.div`
   position: relative;
   background: #11161a;
   box-shadow: ${shadow[2]};
   z-index: 100;
`;

interface HeaderProps {
   breakpoints: any;
   $inMobileSearch?: boolean;
   $hideHeader?: boolean;
}

const HeaderContainer = withBreakpoints(styled.div.attrs(() => ({
   className: 'mainHeader',
}))<HeaderProps>`
   position: relative;
   display: flex;
   background-color: #11161a;
   height: 68px;
   width: 1400px;
   max-width: 100%;
   margin-inline: auto;

   @media (max-width: 1440px) {
      padding: 0 ${space[3]};
   }

   ${props =>
      props.$hideHeader &&
      css`
         display: none !important;
      `}
   ${props =>
      props.breakpoints.isMobile() &&
      props.$inMobileSearch &&
      css`
         ${Section} {
            opacity: 0;
            transform: translateY(-100%);
            pointer-events: none;
            visibility: hidden;
         }
      `}
`);

const LogoContainer = styled.a`
   display: flex;
   margin-inline: ${space[4]};

   @media (max-width: 374px) {
      margin-inline: 0;
      transform: scale(0.85);
   }
`;

const Section = styled.div<{ $right?: boolean }>`
   display: flex;
   align-items: center;
   transition: all 0.25s;

   ${props =>
      props.$right &&
      css`
         gap: ${space[4]};
         flex: 1;
         justify-content: flex-end;
         margin-inline: ${space[2]};

         @media (max-width: 374px) {
            gap: ${space[2]};
            justify-content: space-around;
            max-width: 150px;
            margin-left: auto;
         }
      `}
`;

const LogoComponent = ({ isPro }: { isPro: boolean }) => (
   <LogoContainer href="/" aria-label="home page">
      {isPro ? <ProLogo /> : <Logo />}
   </LogoContainer>
);

const mapStateToProps = (state: any) => ({
   headerState: state.headerState,
});

const mapDispatchToProps = {
   toggleMobileMenu: HeaderActions.toggleMobileMenu,
};

const Header = (props: any) => {
   const { headerState, isOnCart, links, stores, dropdowns, toggleMobileMenu, hideHeader, isPro } =
      props;

   const breakpoints = useContext(BreakpointsContext);

   const [modalIsOpen] = useModalIsOpen();

   const mobileMenuClicked = useCallback(() => {
      toggleMobileMenu();
      // @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
      document.querySelector('#menuIcon').focus();
   }, [toggleMobileMenu]);

   const {
      isOpen: isWorkBenchOpen,
      onOpen: onWorkbenchOpen,
      onClose: onWorkbenchClose,
   } = useDisclosure();

   if (breakpoints.isMobileTablet()) {
      // eslint-disable-next-line react-hooks/immutability
      document.body.style.overflow = headerState.displayMobileMenu ? 'hidden' : 'auto';

      return (
         <FocusLock disabled={!!(!headerState.displayMobileMenu || modalIsOpen)}>
            <HeaderContainer
               $inMobileSearch={headerState.displayMobileSearch}
               $hideHeader={hideHeader}
            >
               {breakpoints.isMobile() && <InstantMobileSearch isOnCart={isOnCart} />}
               <Section>
                  <MenuIcon isActive={headerState.displayMobileMenu} onClick={mobileMenuClicked} />
                  <LogoComponent isPro={isPro} />
               </Section>
               <Section $right>
                  {breakpoints.isTablet() && <InstantSearchBar isOnCart={isOnCart} />}
                  <Dropdowns
                     dropdowns={dropdowns}
                     isOnCart={isOnCart}
                     isWorkBenchOpen={isWorkBenchOpen}
                     onWorkbenchOpen={onWorkbenchOpen}
                     onWorkbenchClose={onWorkbenchClose}
                  />
               </Section>
               <MobileMenu
                  links={links}
                  languageDropdown={dropdowns.languageDropdown}
                  stores={stores}
                  isOnCart={isOnCart}
                  onWorkbenchOpen={onWorkbenchOpen}
               />
            </HeaderContainer>
         </FocusLock>
      );
   }

   return (
      <HeaderWrapper>
         <HeaderContainer $hideHeader={hideHeader}>
            <Section>
               <LogoComponent isPro={isPro} />
            </Section>
            <Headings links={links} stores={stores} />
            <Section $right>
               <InstantSearchBar isOnCart={isOnCart} />
               <Dropdowns
                  dropdowns={dropdowns}
                  isOnCart={isOnCart}
                  isWorkBenchOpen={isWorkBenchOpen}
                  onWorkbenchOpen={onWorkbenchOpen}
                  onWorkbenchClose={onWorkbenchClose}
               />
            </Section>
         </HeaderContainer>
      </HeaderWrapper>
   );
};

export default connect(mapStateToProps, mapDispatchToProps)(Header);
